import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { MessageSquare, Zap, Pause, Play, RefreshCw, Loader2, ChefHat, Mail, Users } from "lucide-react";
import { API, authFetch } from "@/App";

export function MilesAgentPanel({ open, onOpenChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [dmSending, setDmSending] = useState(false);
  const [introducing, setIntroducing] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/miles/status`);
      if (res.ok) {
        setStatus(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch Miles status:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchStatus();
  }, [open, fetchStatus]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const res = await authFetch(`${API}/miles/trigger`, { method: "POST" });
      if (res.ok) {
        await fetchStatus();
      }
    } catch (err) {
      console.error("Failed to trigger Miles:", err);
    } finally {
      setTriggering(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await authFetch(`${API}/miles/toggle`, { method: "POST" });
      if (res.ok) {
        await fetchStatus();
      }
    } catch (err) {
      console.error("Failed to toggle Miles:", err);
    } finally {
      setToggling(false);
    }
  };

  const handleDmNewMembers = async () => {
    setDmSending(true);
    try {
      const res = await authFetch(`${API}/miles/dm-new-members`, { method: "POST" });
      if (res.ok) {
        await fetchStatus();
      }
    } catch (err) {
      console.error("Failed to send DMs:", err);
    } finally {
      setDmSending(false);
    }
  };

  const handleIntroduce = async () => {
    setIntroducing(true);
    try {
      const res = await authFetch(`${API}/miles/introduce`, { method: "POST" });
      if (res.ok) {
        await fetchStatus();
      }
    } catch (err) {
      console.error("Failed to introduce:", err);
    } finally {
      setIntroducing(false);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return "N/A";
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="miles-agent-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-green-400">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/23cc3132-5564-4dc3-9937-bde34e47f086/images/16f4e7cecf4ad6f1e23ead81bc93225446a64cfc7157ab92ccd668ffeb99bea2.png"
                alt="Miles"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="text-lg">Miles — Community Agent</span>
              {status && (
                <Badge className={`ml-2 ${status.enabled ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-600"}`}>
                  {status.enabled ? "Active" : "Paused"}
                </Badge>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>
            Philly-born dessert chef driving engagement on the discussion board
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pt-2">
          {loading && !status ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : status ? (
            <>
              {/* Controls */}
              <div className="flex flex-col gap-3 p-4 bg-neutral-50 rounded-lg border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={status.enabled}
                      onCheckedChange={handleToggle}
                      disabled={toggling}
                      data-testid="miles-toggle"
                    />
                    <span className="text-sm font-medium">
                      {status.enabled ? "Miles is posting automatically" : "Miles is paused"}
                    </span>
                  </div>
                  <Button
                    onClick={handleTrigger}
                    disabled={triggering}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="miles-trigger-btn"
                  >
                    {triggering ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Posting...</>
                    ) : (
                      <><Zap className="w-4 h-4 mr-1" /> Trigger Post</>
                    )}
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleDmNewMembers}
                    disabled={dmSending}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-blue-200 text-blue-700 hover:bg-blue-50"
                    data-testid="miles-dm-btn"
                  >
                    {dmSending ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-1" /> DM New Members</>
                    )}
                  </Button>
                  <Button
                    onClick={handleIntroduce}
                    disabled={introducing}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-purple-200 text-purple-700 hover:bg-purple-50"
                    data-testid="miles-intro-btn"
                  >
                    {introducing ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Posting...</>
                    ) : (
                      <><Users className="w-4 h-4 mr-1" /> Introduce to All</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="border-neutral-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-neutral-900">{status.stats.posts_today}</p>
                    <p className="text-xs text-neutral-500">Posts Today</p>
                  </CardContent>
                </Card>
                <Card className="border-neutral-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-neutral-900">{status.stats.comments_today}</p>
                    <p className="text-xs text-neutral-500">Replies Today</p>
                  </CardContent>
                </Card>
                <Card className="border-neutral-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-neutral-900">{status.stats.dms_today || 0}</p>
                    <p className="text-xs text-neutral-500">DMs Today</p>
                  </CardContent>
                </Card>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Card className="border-neutral-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{status.stats.total_posts}</p>
                    <p className="text-xs text-neutral-500">Total Posts</p>
                  </CardContent>
                </Card>
                <Card className="border-neutral-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">{status.stats.total_comments}</p>
                    <p className="text-xs text-neutral-500">Total Replies</p>
                  </CardContent>
                </Card>
                <Card className="border-neutral-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{status.stats.total_dms || 0}</p>
                    <p className="text-xs text-neutral-500">Total DMs</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Posts */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-green-600" />
                    Recent Posts by Miles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {status.recent_posts.length === 0 ? (
                    <p className="text-sm text-neutral-500 text-center py-4">
                      No posts yet. Click "Trigger Now" to get Miles started!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {status.recent_posts.map((post) => (
                        <div key={post.post_id} className="p-3 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-sm text-neutral-800 line-clamp-3">{post.content}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">{post.category}</Badge>
                            <span className="text-xs text-neutral-400">{formatTime(post.created_at)}</span>
                            {post.is_ai_generated && <Badge className="text-xs bg-purple-100 text-purple-600">AI</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Replies */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                    Recent Replies by Miles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {status.recent_comments.length === 0 ? (
                    <p className="text-sm text-neutral-500 text-center py-4">
                      No replies yet. Miles will reply to member posts automatically.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {status.recent_comments.map((comment) => (
                        <div key={comment.comment_id} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-sm text-neutral-800 line-clamp-3">{comment.content}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-neutral-400">on post {comment.post_id}</span>
                            <span className="text-xs text-neutral-400">{formatTime(comment.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent DMs */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Mail className="w-4 h-4 text-green-600" />
                    Recent Welcome DMs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(!status.recent_dms || status.recent_dms.length === 0) ? (
                    <p className="text-sm text-neutral-500 text-center py-4">
                      No DMs sent yet. Click "DM New Members" to get started!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {status.recent_dms.map((dm) => (
                        <div key={dm.message_id} className="p-3 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-sm text-neutral-800 line-clamp-3">{dm.content}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-neutral-400">to {dm.recipient_id}</span>
                            <span className="text-xs text-neutral-400">{formatTime(dm.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Miles Info */}
              <div className="p-4 bg-neutral-900 rounded-lg text-white">
                <div className="flex items-center gap-3 mb-3">
                  <ChefHat className="w-5 h-5 text-green-400" />
                  <span className="font-semibold">About Miles</span>
                </div>
                <p className="text-sm text-neutral-300 leading-relaxed">
                  Miles is an AI-powered community member from Philly. He's a dessert chef and Philly sports fanatic 
                  who engages members by asking about their businesses, sharing creative recipes, and creating 
                  a welcoming vibe on the discussion board. He posts 3-5 times daily — mixing business icebreakers, 
                  recipe shares, and member engagement threads.
                </p>
              </div>
            </>
          ) : (
            <p className="text-center py-8 text-neutral-500">Failed to load Miles status</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
